import * as XLSX from "xlsx";
import api from "./api";

const DEFAULT_PAGE_SIZE = 500;

const ASSOCIATION_HEADERS = {
  orgao: ["ORGAO", "ORGÃO", "UNIDADE"],
  siape: ["SIAPE", "MATRICULA", "MATRÍCULA"],
  ufUpag: ["UF_UPAG", "UF", "UPAG"],
  nome: ["NOME", "NOME DO SERVIDOR"],
  cpf: ["CPF"],
  valor: ["VALOR", "VALOR_MENSAL", "TAXA"],
};

const buildParams = () =>
  new URLSearchParams({
    user: localStorage.getItem("user") || "",
    gmt: "-04:00",
  }).toString();

const buildTimestamp = () => new Date().toISOString();

const normalizeDigits = (value) => String(value ?? "").replace(/\D/g, "");

const normalizeText = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .replace(/\s+/g, " ")
    .toUpperCase();

const normalizeMoney = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return Number(value.toFixed(2));
  }

  const raw = String(value).replace(/[^\d,.-]/g, "").trim();
  if (!raw) {
    return null;
  }

  const normalized = raw.includes(",") && raw.includes(".")
    ? raw.replace(/\./g, "").replace(",", ".")
    : raw.replace(",", ".");

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : null;
};

const sameMoney = (left, right) => {
  const leftValue = normalizeMoney(left);
  const rightValue = normalizeMoney(right);

  if (leftValue === null && rightValue === null) {
    return true;
  }

  if (leftValue === null || rightValue === null) {
    return false;
  }

  return Math.abs(leftValue - rightValue) < 0.001;
};

const stripUndefined = (value) =>
  Object.fromEntries(
    Object.entries(value || {}).filter(([, entry]) => entry !== undefined),
  );

const pickPreviousValues = (source, patch) =>
  Object.keys(patch || {}).reduce((accumulator, key) => {
    accumulator[key] = source?.[key] ?? null;
    return accumulator;
  }, {});

const getSocioId = (socio) => socio?.autocod ?? socio?.AUTOCOD ?? null;

const getSelectedCompanyId = () => {
  const raw = localStorage.getItem("cdemp");
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const getDefaultClientCreatePayload = (row) => ({
  cdemp: getSelectedCompanyId(),
  tipocli: "F",
  decli: row.nome || "",
  fantcli: row.nome || "",
  cnpj_cpfcli: row.cpf || "",
  ativocli: "S",
  vlrmensal: row.valor,
  estcli: localStorage.getItem("estemp") || "",
  cidcli: localStorage.getItem("cidemp") || "",
  baicli: localStorage.getItem("baiemp") || "",
});

const getDefaultSocioCreatePayload = (row, clientId = null) => ({
  CDEMP: getSelectedCompanyId(),
  CDCLI: clientId || "",
  MATRICULA: row.siape || "",
  UNIDADE: row.orgao || "",
  SITUACAO: "Ativo",
  OPCONTRIB: "Desconto em folha",
});

const getCellValue = (row, aliases) => {
  const entry = Object.entries(row).find(([key]) =>
    aliases.includes(normalizeText(key)),
  );

  return entry ? entry[1] : "";
};

const getCandidateUnit = (candidate) =>
  normalizeText(
    candidate?.socio?.unidade ||
      candidate?.socio?.UNIDADE ||
      candidate?.client?.orgao_do_governo ||
      "",
  );

const getCandidateCpf = (candidate) =>
  normalizeDigits(candidate?.client?.cnpj_cpfcli);

const chooseCandidateFromList = (entries, row) => {
  if (!entries.length) {
    return { candidate: null, ambiguous: false };
  }

  if (entries.length === 1) {
    return { candidate: entries[0], ambiguous: false };
  }

  const byUnit = row.orgao
    ? entries.filter((entry) => getCandidateUnit(entry) === row.orgao)
    : entries;

  if (byUnit.length === 1) {
    return { candidate: byUnit[0], ambiguous: false };
  }

  const byCpf = row.cpf
    ? byUnit.filter((entry) => getCandidateCpf(entry) === row.cpf)
    : byUnit;

  if (byCpf.length === 1) {
    return { candidate: byCpf[0], ambiguous: false };
  }

  return { candidate: null, ambiguous: true };
};

const parseSpreadsheetRows = (workbook, sheetName) => {
  const worksheet = workbook.Sheets[sheetName];
  if (!worksheet) {
    throw new Error("Aba da planilha nao encontrada.");
  }

  const records = XLSX.utils.sheet_to_json(worksheet, {
    defval: "",
    raw: false,
  });

  return records
    .map((record, index) => {
      const row = {
        rowNumber: index + 2,
        orgao: normalizeText(getCellValue(record, ASSOCIATION_HEADERS.orgao)),
        siape: normalizeDigits(getCellValue(record, ASSOCIATION_HEADERS.siape)),
        ufUpag: normalizeText(getCellValue(record, ASSOCIATION_HEADERS.ufUpag)),
        nome: normalizeText(getCellValue(record, ASSOCIATION_HEADERS.nome)),
        cpf: normalizeDigits(getCellValue(record, ASSOCIATION_HEADERS.cpf)),
        valor: normalizeMoney(getCellValue(record, ASSOCIATION_HEADERS.valor)),
      };

      return row;
    })
    .filter((row) => row.nome || row.cpf || row.siape);
};

const mergeClientesESocios = (clientes, socios) => {
  const firstSocioByClient = new Map();

  socios
    .slice()
    .sort((left, right) => Number(left.autocod || left.AUTOCOD || 0) - Number(right.autocod || right.AUTOCOD || 0))
    .forEach((socio) => {
      const clientId = String(socio.cdcli ?? socio.CDCLI ?? "");
      if (clientId && !firstSocioByClient.has(clientId)) {
        firstSocioByClient.set(clientId, socio);
      }
    });

  return clientes.map((client) => {
    const socio = firstSocioByClient.get(String(client.cdcli)) || null;
      return {
        ...client,
        matricula: socio?.matricula ?? socio?.MATRICULA ?? "",
        unidade: socio?.unidade ?? socio?.UNIDADE ?? "",
        curso:
          socio?.curso ??
          socio?.CURSO ??
          socio?.departamento ??
          socio?.DEPARTAMENTO ??
          "",
        situacao: socio?.situacao ?? socio?.SITUACAO ?? "",
        departamento: socio?.departamento ?? socio?.DEPARTAMENTO ?? "",
      opcontrib: socio?.opcontrib ?? socio?.OPCONTRIB ?? "",
      dtfilicao: socio?.dtfilicao ?? socio?.DTFILICAO ?? null,
      socioAutocod: socio?.autocod ?? socio?.AUTOCOD ?? null,
      dataDesativacao: client.dataultalt || client.updatedat || null,
    };
  });
};

export const readWorkbookFromFile = async (file) => {
  const buffer = await file.arrayBuffer();
  return XLSX.read(buffer, { type: "array", cellDates: true });
};

export const getWorkbookSheetNames = (workbook) => workbook.SheetNames || [];

export const getDefaultWorkbookSheet = (workbook) => {
  const sheetNames = getWorkbookSheetNames(workbook);
  return sheetNames.length ? sheetNames[sheetNames.length - 1] : "";
};

export const fetchAllPaginatedRows = async (endpoint, extraParams = {}) => {
  const allRows = [];
  let page = 1;
  let total = null;

  while (total === null || allRows.length < total) {
    const response = await api.get(endpoint, {
      params: {
        ...extraParams,
        page,
        pageSize: DEFAULT_PAGE_SIZE,
      },
    });

    const count = Number(response.data?.count || 0);
    const rows = Array.isArray(response.data?.rows) ? response.data.rows : [];

    total = count;
    allRows.push(...rows);

    if (!rows.length || allRows.length >= count) {
      break;
    }

    page += 1;
  }

  return allRows;
};

export const loadAllClientesSocios = async () => {
  const [clientes, socios] = await Promise.all([
    fetchAllPaginatedRows("/t_cli"),
    fetchAllPaginatedRows("/t_socio"),
  ]);

  return {
    clientes,
    socios,
    rows: mergeClientesESocios(clientes, socios),
  };
};

export const buildAssociationImportPreview = ({
  workbook,
  sheetName,
  clientes,
  socios,
}) => {
  const parsedRows = parseSpreadsheetRows(workbook, sheetName);
  const candidates = mergeClientesESocios(clientes, socios).map((row) => ({
    client: row,
    socio: row.socioAutocod
      ? socios.find(
          (item) =>
            String(item.autocod ?? item.AUTOCOD ?? "") ===
            String(row.socioAutocod),
        ) || null
      : null,
  }));

  const bySiape = new Map();
  const byCpf = new Map();
  const byName = new Map();
  const byNameAndUnit = new Map();

  const pushIndex = (map, key, candidate) => {
    if (!key) {
      return;
    }
    const current = map.get(key) || [];
    current.push(candidate);
    map.set(key, current);
  };

  candidates.forEach((candidate) => {
    pushIndex(
      bySiape,
      normalizeDigits(candidate.socio?.matricula ?? candidate.socio?.MATRICULA),
      candidate,
    );
    pushIndex(byCpf, normalizeDigits(candidate.client.cnpj_cpfcli), candidate);
    pushIndex(byName, normalizeText(candidate.client.decli), candidate);
    pushIndex(
      byNameAndUnit,
      `${normalizeText(candidate.client.decli)}::${getCandidateUnit(candidate)}`,
      candidate,
    );
  });

  const previewItems = [];
  const matchedClientIds = new Set();

  parsedRows.forEach((row) => {
    const attempts = [
      { label: "SIAPE", entries: bySiape.get(row.siape) || [] },
      { label: "CPF", entries: byCpf.get(row.cpf) || [] },
      {
        label: "NOME+UNIDADE",
        entries: byNameAndUnit.get(`${row.nome}::${row.orgao}`) || [],
      },
      { label: "NOME", entries: byName.get(row.nome) || [] },
    ];

    let matchedCandidate = null;
    let matchedBy = "";
    let ambiguous = false;

    attempts.some((attempt) => {
      if (!attempt.entries.length) {
        return false;
      }

      const result = chooseCandidateFromList(attempt.entries, row);
      if (result.candidate) {
        matchedCandidate = result.candidate;
        matchedBy = attempt.label;
        return true;
      }

      if (result.ambiguous) {
        ambiguous = true;
        matchedBy = `${attempt.label} ambíguo`;
        return true;
      }

      return false;
    });

    if (!matchedCandidate) {
      const clientCreatePayload = ambiguous
        ? null
        : getDefaultClientCreatePayload(row);
      const socioCreatePayload = ambiguous
        ? null
        : getDefaultSocioCreatePayload(row);

      previewItems.push({
        ...row,
        status: ambiguous ? "ambiguous" : "not_found",
        matchedBy,
        client: null,
        socio: null,
        clientPatch: null,
        socioPatch: null,
        clientCreatePayload,
        socioCreatePayload,
      });
      return;
    }

    const clientPatch = {};
    const socioPatch = {};

    if (normalizeText(matchedCandidate.client.ativocli) !== "S") {
      clientPatch.ativocli = "S";
    }

    if (!sameMoney(matchedCandidate.client.vlrmensal, row.valor)) {
      clientPatch.vlrmensal = row.valor;
    }

    if (Object.keys(clientPatch).length) {
      clientPatch.updatedat = buildTimestamp();
    }

    if (matchedCandidate.socio) {
      if (
        row.siape &&
        normalizeDigits(
          matchedCandidate.socio.matricula ?? matchedCandidate.socio.MATRICULA,
        ) !== row.siape
      ) {
        socioPatch.MATRICULA = row.siape;
      }

      if (row.orgao && getCandidateUnit(matchedCandidate) !== row.orgao) {
        socioPatch.UNIDADE = row.orgao;
      }
    }

    const socioCreatePayload = matchedCandidate.socio
      ? null
      : {
          CDEMP: matchedCandidate.client.cdemp,
          CDCLI: matchedCandidate.client.cdcli,
          MATRICULA: row.siape || "",
          UNIDADE: row.orgao || "",
          SITUACAO: "Ativo",
          OPCONTRIB: "Desconto em folha",
        };

    previewItems.push({
      ...row,
      status: "matched",
      matchedBy,
      client: matchedCandidate.client,
      socio: matchedCandidate.socio,
      clientPatch: Object.keys(clientPatch).length ? clientPatch : null,
      socioPatch: Object.keys(socioPatch).length ? socioPatch : null,
      clientCreatePayload: null,
      socioCreatePayload,
    });

    matchedClientIds.add(String(matchedCandidate.client.cdcli));
  });

  const deactivationItems = candidates
    .filter((candidate) => candidate.socio)
    .filter((candidate) => !matchedClientIds.has(String(candidate.client.cdcli)))
    .filter((candidate) => normalizeText(candidate.client.ativocli) === "S")
    .map((candidate) => {
      const timestamp = buildTimestamp();

      return {
        client: candidate.client,
        payload: {
          ativocli: "N",
          updatedat: timestamp,
          dataultalt: timestamp,
        },
      };
    });

  const includedRecords = previewItems.filter(
    (item) => item.clientCreatePayload,
  ).length;
  const matchedRecords = previewItems.filter(
    (item) => item.status === "matched",
  ).length;
  const unresolvedRecords = previewItems.filter(
    (item) => item.status === "ambiguous",
  ).length;
  const notFoundItems = previewItems.filter(
    (item) => item.status === "not_found" && item.clientCreatePayload,
  );

  return {
    sheetName,
    parsedRows,
    previewItems,
    deactivationItems,
    notFoundItems,
    summary: {
      totalRows: parsedRows.length,
      matched: matchedRecords,
      includedRecords,
      unresolved: unresolvedRecords,
      clientUpdates: previewItems.filter((item) => item.clientPatch).length,
      socioUpdates: previewItems.filter((item) => item.socioPatch).length,
      socioCreates: previewItems.filter(
        (item) => item.status === "matched" && item.socioCreatePayload,
      ).length,
      clientCreates: includedRecords,
      deactivations: deactivationItems.length,
    },
  };
};

export const applyAssociationImportPreview = async (preview) => {
  const params = buildParams();
  const result = {
    clientUpdates: 0,
    socioUpdates: 0,
    socioCreates: 0,
    clientCreates: 0,
    deactivations: 0,
    createdClients: [],
    errors: [],
    rollbackPlan: {
      clientCreates: [],
      clientUpdates: [],
      socioUpdates: [],
      socioCreates: [],
      receivablesUndoToken: null,
    },
  };

  for (const item of preview.previewItems.filter(
    (entry) => entry.status === "matched" || entry.clientCreatePayload,
  )) {
    try {
      if (item.clientPatch) {
        await api.put(`/t_cli/${item.client.cdcli}?${params}`, item.clientPatch);
        result.clientUpdates += 1;
        result.rollbackPlan.clientUpdates.push({
          id: item.client.cdcli,
          previousValues: stripUndefined(
            pickPreviousValues(item.client, item.clientPatch),
          ),
        });
      }

      let clientId = item.client?.cdcli || null;

      if (item.clientCreatePayload) {
        const response = await api.post(`/t_cli?${params}`, item.clientCreatePayload);
        clientId = response.data?.cdcli || null;
        result.clientCreates += 1;

        if (!clientId) {
          throw new Error("Codigo do cliente criado nao retornado pelo backend.");
        }

        result.rollbackPlan.clientCreates.push({
          id: clientId,
        });
        result.createdClients.push({
          cdcli: clientId,
          cdemp: response.data?.cdemp ?? item.clientCreatePayload.cdemp,
          nome: response.data?.decli ?? item.clientCreatePayload.decli,
          cpf: response.data?.cnpj_cpfcli ?? item.clientCreatePayload.cnpj_cpfcli,
          matricula: item.siape || "",
          valor: item.valor,
        });
      }

      const socioId = getSocioId(item.socio);

      if (item.socioPatch && socioId) {
        await api.put(
          `/t_socio/${socioId}?${params}`,
          item.socioPatch,
        );
        result.socioUpdates += 1;
        result.rollbackPlan.socioUpdates.push({
          id: socioId,
          previousValues: stripUndefined(
            pickPreviousValues(item.socio, item.socioPatch),
          ),
        });
      }

      if (item.socioCreatePayload) {
        if (!clientId) {
          throw new Error("Cliente nao identificado para criar o vinculo de socio.");
        }

        const response = await api.post(`/t_socio?${params}`, {
          ...item.socioCreatePayload,
          CDCLI: clientId,
        });
        const createdSocioId = getSocioId(response.data);
        result.socioCreates += 1;

        if (createdSocioId) {
          result.rollbackPlan.socioCreates.push({
            id: createdSocioId,
          });
        }
      }
    } catch (error) {
        result.errors.push({
        type: item.clientCreatePayload ? "created_row" : "matched_row",
        rowNumber: item.rowNumber,
        nome: item.nome,
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Falha ao aplicar atualizacao.",
      });
    }
  }

  for (const item of preview.deactivationItems) {
    try {
      await api.put(`/t_cli/${item.client.cdcli}?${params}`, item.payload);
      result.deactivations += 1;
      result.rollbackPlan.clientUpdates.push({
        id: item.client.cdcli,
        previousValues: stripUndefined(
          pickPreviousValues(item.client, item.payload),
        ),
      });
    } catch (error) {
      result.errors.push({
        type: "deactivation",
        clientId: item.client.cdcli,
        nome: item.client.decli,
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Falha ao desativar cliente.",
      });
    }
  }

  return result;
};

export const undoAssociationImport = async (rollbackPlan) => {
  if (!rollbackPlan) {
    throw new Error("Nao ha importacao para reverter.");
  }

  const params = buildParams();

  if (rollbackPlan.receivablesUndoToken) {
    await api.post(
      `/importacao-associados/recebiveis/reverter?${params}`,
      {
        undoToken: rollbackPlan.receivablesUndoToken,
      },
    );
  }

  for (const item of [...(rollbackPlan.socioCreates || [])].reverse()) {
    await api.delete(`/t_socio/${item.id}?${params}`);
  }

  for (const item of [...(rollbackPlan.clientCreates || [])].reverse()) {
    await api.delete(`/t_cli/${item.id}?${params}`);
  }

  for (const item of [...(rollbackPlan.socioUpdates || [])].reverse()) {
    await api.put(`/t_socio/${item.id}?${params}`, item.previousValues);
  }

  for (const item of [...(rollbackPlan.clientUpdates || [])].reverse()) {
    await api.put(`/t_cli/${item.id}?${params}`, item.previousValues);
  }
};

export const requestAssociationReceivablesGeneration = async (
  preview,
  importResult = null,
) => {
  const params = buildParams();
  const createdClients = Array.isArray(importResult?.createdClients)
    ? importResult.createdClients
    : [];

  const payload = {
    competencia: preview.sheetName,
    origem: "IMPORTACAO_ASSOCIADOS",
    clientesAtivos: [
      ...preview.previewItems
      .filter((item) => item.status === "matched")
      .map((item) => ({
        cdcli: item.client?.cdcli,
        cdemp: item.client?.cdemp,
        nome: item.client?.decli || item.nome,
        cpf: item.client?.cnpj_cpfcli || item.cpf,
        matricula: item.siape,
        valor: item.valor,
      })),
      ...createdClients,
    ],
    clientesDesativados: preview.deactivationItems.map((item) => ({
      cdcli: item.client?.cdcli,
      cdemp: item.client?.cdemp,
      nome: item.client?.decli,
      cpf: item.client?.cnpj_cpfcli,
      valor: item.client?.vlrmensal,
    })),
  };

  const response = await api.post(
    `/importacao-associados/recebiveis?${params}`,
    payload,
  );

  return {
    created:
      Number(response.data?.created) || payload.clientesAtivos.length,
    cancelled:
      Number(response.data?.cancelled) || payload.clientesDesativados.length,
    undoToken: response.data?.undoToken || null,
  };
};

export const filterInactiveRowsByDate = (rows, options = {}) => {
  const {
    buscaTexto = "",
    filtros = {},
    dataInicial = "",
    dataFinal = "",
  } = options;

  const start = dataInicial ? new Date(`${dataInicial}T00:00:00`) : null;
  const end = dataFinal ? new Date(`${dataFinal}T23:59:59.999`) : null;
  const searchTerm = normalizeText(buscaTexto);
  const cpf = normalizeDigits(filtros.cpf);
  const unidade = normalizeText(filtros.unidade);
  const curso = normalizeText(filtros.curso);
  const situacao = normalizeText(filtros.situacao);
  const departamento = normalizeText(filtros.departamento);
  const tipoContribuicao = normalizeText(filtros.tipocontribuicao);

  return rows.filter((row) => {
    if (normalizeText(row.ativocli) === "S") {
      return false;
    }

    const referenceDate = row.dataultalt || row.updatedat || row.dataDesativacao;
    if (!referenceDate) {
      return false;
    }

    const parsedDate = new Date(referenceDate);
    if (Number.isNaN(parsedDate.getTime())) {
      return false;
    }

    if (start && parsedDate < start) {
      return false;
    }

    if (end && parsedDate > end) {
      return false;
    }

    if (
      searchTerm &&
      ![row.decli, row.fantcli, row.cidcli, row.baicli]
        .map(normalizeText)
        .some((value) => value.includes(searchTerm))
    ) {
      return false;
    }

    if (cpf && normalizeDigits(row.cnpj_cpfcli) !== cpf) {
      return false;
    }

    if (unidade && normalizeText(row.unidade) !== unidade) {
      return false;
    }

    if (curso && normalizeText(row.curso) !== curso) {
      return false;
    }

    if (situacao && normalizeText(row.situacao) !== situacao) {
      return false;
    }

    if (departamento && normalizeText(row.departamento) !== departamento) {
      return false;
    }

    if (tipoContribuicao && normalizeText(row.opcontrib) !== tipoContribuicao) {
      return false;
    }

    return true;
  });
};
